import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { CSSTransition } from 'react-transition-group';

import { ChevronRight, ChevronLeft } from '@src/components/common/icon';

const fadeInRight = keyframes`
  from {
    opacity: 0;
		transform: translateX(-500px);
  }
  to {
    opacity: 1;
		transform: translateX(0px);
  }
`;

const fadeOutRight = keyframes`
  from {
    opacity: 1;
		transform: translateX(0px);
  }
  to {
    opacity: 0;
		transform: translateX(500px);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
		transform: translateX(500px);
  }
  to {
    opacity: 1;
		transform: translateX(0px);
  }
`;

const fadeOutLeft = keyframes`
  from {
    opacity: 1;
		transform: translateX(0px);
  }
  to {
    opacity: 0;
		transform: translateX(-500px);
  }
`;

const CarouselWrapper = styled.div`
	background: #000;
	width: 100%;
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	overflow: hidden;

	@media
		${props => props.theme.device.lgDesktop},
		${props => props.theme.device.desktop},
		${props => props.theme.device.tablet} {
			height: ${props => (!props.carouselSize.width ? 417 : (props.carouselSize.width / 16) * 9)}px;
	}

	@media
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile} {
			height: calc((100vw / 16) * 9);
	}

	.carousel__css-transition {
		&-enter {
			animation-name: ${props => (props.rotateDirection === 'right' ? fadeInRight : fadeInLeft)};
			animation-duration: 0.8s;
			animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
			animation-fill-mode: forwards;
		}
		&-exit {
			animation-name: ${props => (props.rotateDirection === 'right' ? fadeOutRight : fadeOutLeft)};
			animation-duration: 0.8s;
			animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
			animation-fill-mode: forwards;
		}
	}
`;

const Image = styled.img`
	position: absolute;
	opacity: ${props => (props.isActive ? 1 : 0)};
	object-fit: cover;
`;

const ChevronButtonGroup = styled.div`
  z-index: 1;
	width: 100%;
  display: flex;
  align-items: center;
	justify-content: space-between;

	.chevron-button-group__button {
		cursor: pointer;
	}
`;

const Indicators = styled.div`
	position: absolute;
	bottom: 12px;
	display: inline-flex;
	justify-content: center;
	align-items: flex-end;

	& > *:not(:first-child) {
		margin-left: 8px;
	}
`;

const IndicatorDot = styled.div`
	z-index: 1;
	width: 10px;
	height: 10px;
	background: ${props => (props.isActive ? props.theme.colors.primary : '#fff')};
	border: 1px solid ${props => (props.isActive ? props.theme.colors.primary : '#DDD')};
	border-radius: 100%;
	cursor: pointer;
`;

const clearAllInterval = tempTimeoutId => {
	for (let i = 1; i < tempTimeoutId; i += 1) { window.clearInterval(i); }
};

const ROTATE_DIRECTION_RIGHT = 'right';
const ROTATE_DIRECTION_LEFT = 'left';

interface IImage {
	link: string;
	altTitle: string;
}

interface ICarousel {
	maxImageAmount: number;
	imageLinkList: IImage[];
}

const Carousel = ({ maxImageAmount, imageLinkList = [] }: ICarousel): JSX.Element => {
	const carouselRef = useRef(null);
	const imageList = imageLinkList.filter((_, index) => index < maxImageAmount);
	const [timeoutId, setTimeoutId] = useState(null);
	const [rotateDirection, setRotateDirection] = useState(ROTATE_DIRECTION_RIGHT);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [carouselSize, setCarouselSize] = useState({
		width: 0,
		height: 0,
	});

	const handleRestartRotateInterval = () => {
		const tempTimeoutId = setInterval(() => {
			setRotateDirection(ROTATE_DIRECTION_RIGHT);
			setCurrentIndex(prev => (prev + 1) % imageList.length);
		}, 5000);
		setTimeoutId(tempTimeoutId);
		clearAllInterval(tempTimeoutId);
	};

	const handleClickIndicator = clickedIndex => {
		setCurrentIndex(clickedIndex);
		handleRestartRotateInterval();
	};

	const handleClickChevronRight = () => {
		setCurrentIndex(prev => (prev + 1) % imageList.length);
		handleRestartRotateInterval();
	};

	const handleClickChevronLeft = () => {
		setRotateDirection(ROTATE_DIRECTION_LEFT);
		setCurrentIndex(prev => (prev > 0 ? prev - 1 : imageList.length - 1));
		handleRestartRotateInterval();
	};

	const handleResize = () => {
		const carouselElem = carouselRef.current;
		setCarouselSize({
			width: carouselElem.clientWidth,
			height: carouselElem.clientHeight,
		});
	};

	useEffect(() => {
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		let tempTimeoutId;
		if (imageList.length > 1) {
			tempTimeoutId = setInterval(() => {
				setCurrentIndex(prev => (prev + 1) % imageList.length);
			}, 5000);
			setTimeoutId(tempTimeoutId);
		}

		return () => {
			clearAllInterval(timeoutId);
		};
	}, []);

	return (
		<CarouselWrapper
			ref={carouselRef}
			carouselSize={carouselSize}
			rotateDirection={rotateDirection}
		>
			{imageList.length > 1 ? (
				<>
					{
						imageList.map((image, index) => {
							const imageKey = `image-${index}`;
							const isActive = index === currentIndex;

							return (
								<CSSTransition
									key={imageKey}
									in={isActive}
									timeout={10000}
									classNames="carousel__css-transition"
								>
									<Image
										alt={image.altTitle}
										src={image.link}
										isActive={isActive}
										width={carouselSize.width}
										height={(carouselSize.width / 16) * 9}
									/>
								</CSSTransition>
							);
						})
					}
				</>
			) : (
				<Image
					alt={imageList[0]?.altTitle}
					src={imageList[0]?.link}
					isActive
					width={carouselSize.width}
					height={(carouselSize.width / 16) * 9}
				/>
			)}
			{imageList.length > 1 && (
				<>
					<ChevronButtonGroup>
						<ChevronLeft
							className="chevron-button-group__button"
							onClick={handleClickChevronLeft}
						/>
						<ChevronRight
							className="chevron-button-group__button"
							onClick={handleClickChevronRight}
						/>
					</ChevronButtonGroup>
					<Indicators>
						{
							imageList.map((_, index) => {
								const dotKey = `dot-${index}`;
								const isActive = index === currentIndex;

								return (
									<IndicatorDot
										key={dotKey}
										isActive={isActive}
										onClick={() => handleClickIndicator(index)}
									/>
								);
							})
						}
					</Indicators>
				</>
			)}
		</CarouselWrapper>
	);
};

export default Carousel;
